import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NzUploadChangeParam, NzUploadFile } from 'ng-zorro-antd/upload';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export interface IInvalidImageSize {
  width: number;
  height: number;
  maxWidth: number;
  maxHeight: number;
  readable: boolean;
}

@UntilDestroy()
@Component({
  selector: 'literax-upload-button-control',
  templateUrl: './upload-button-control.component.html',
  styleUrls: ['./upload-button-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UploadButtonControlComponent),
      multi: true,
    },
  ],
})
export class UploadButtonControlComponent
  implements OnInit, ControlValueAccessor
{
  file: any;
  disabled: boolean;
  @Input() placeholder = 'FORM.FILE_INPUT.SELECT_BUTTON';
  @Input() returnAsBase64 = false;
  @Input() accept: string;
  @Input() maxWidth: number;
  @Input() maxHeight: number;
  @Output() invalidImageSize = new EventEmitter<IInvalidImageSize>();
  // eslint-disable-next-line @typescript-eslint/ban-types
  private onTouched = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  private onChanged = (_: any) => {};

  constructor() {}

  writeValue(obj: any): void {
    if (obj) {
      this.file = obj;
    }
  }
  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {}

  removeFile(): void {
    this.file = null;
    this.onChanged(null);
  }

  onFileChange({ file, fileList }: NzUploadChangeParam): void {
    this.createObjectForFile(file);
  }

  beforeUpload = (file: NzUploadFile, fileList: NzUploadFile[]): boolean => {
    this.createObjectForFile(file);
    return false;
  };

  createObjectForFile(file: any): void {
    if (this.returnAsBase64) {
      this.convertFileToBase64(file)
        .pipe(filter((loadedFile) => !!loadedFile))
        .subscribe((responseFile) => {
          this.setBase64FileIfSizeIsAllowed(file, responseFile);
        });
    } else {
      this.file = file;
      this.onTouched();
      this.onChanged(this.file);
    }
  }

  convertFileToBase64(file: any): Observable<string> {
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        observer.next(reader.result.toString());
      };
      reader.onerror = (error) => {
        observer.error(error);
      };
    });
  }

  private setBase64FileIfSizeIsAllowed(file: any, base64: string): void {
    if (!this.hasSizeLimits()) {
      this.setBase64File(file, base64);
      return;
    }

    this.readImageSize(base64).subscribe(
      (size) => {
        if (size.width > this.maxWidth || size.height > this.maxHeight) {
          this.rejectFile({ ...size, readable: true });
          return;
        }
        this.setBase64File(file, base64);
      },
      () => this.rejectFile({ width: 0, height: 0, readable: false })
    );
  }

  private setBase64File(file: any, base64: string): void {
    this.file = { file, base64 };
    this.onTouched();
    this.onChanged(this.file);
  }

  private rejectFile(size: {
    width: number;
    height: number;
    readable: boolean;
  }): void {
    this.file = null;
    this.onTouched();
    this.onChanged(null);
    this.invalidImageSize.emit({
      ...size,
      maxWidth: this.maxWidth,
      maxHeight: this.maxHeight,
    });
  }

  private hasSizeLimits(): boolean {
    return !!this.maxWidth && !!this.maxHeight;
  }

  private readImageSize(
    base64: string
  ): Observable<{ width: number; height: number }> {
    return new Observable((observer) => {
      const image = new Image();
      image.onload = () => {
        observer.next({
          width: image.naturalWidth || image.width,
          height: image.naturalHeight || image.height,
        });
        observer.complete();
      };
      image.onerror = () => observer.error(new Error('unreadable image'));
      image.src = base64;
    });
  }
}
